import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../Styles/Login.css';
import logo from '../Images/SAVI AI full.png';
import { FiMail, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { PublicClientApplication } from '@azure/msal-browser';

// MSAL configuration
const msalConfig = {
  auth: {
    clientId: 'b759646c-30d1-49fd-95de-f17ed7202705',
    authority: `https://login.microsoftonline.com/8a50a18b-c135-4d02-b21a-bd6b114c6755`,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  }
};

const msalInstance = new PublicClientApplication(msalConfig);

const Login = () => {
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  // Handle Microsoft login
  const handleMicrosoftLogin = async () => {
    try {
      setLoading(true);
      await msalInstance.initialize();

      const loginRequest = {
        scopes: ['openid', 'profile', 'User.Read'],
        prompt: 'login'
      };

      const response = await msalInstance.loginPopup(loginRequest);

      if (response.account) {
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('user', response.account.username);
        localStorage.setItem('authMethod', 'microsoft');
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('rememberMe');
        }
        navigate('/chat', { replace: true });
      }
    } catch (err) {
      setError('Microsoft login failed. Please try again.');
      console.error('Microsoft login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setCredentials(prev => ({
      ...prev,
      email: e.target.value
    }));
  };

  const handlePasswordChange = (e) => {
    setCredentials(prev => ({
      ...prev,
      password: e.target.value
    }));
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === 'Tab') && credentials.email.trim()) {
      e.preventDefault();
      setShowPasswordField(true);
      setTimeout(() => passwordInputRef.current?.focus(), 50);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(process.env.REACT_APP_LOGIN_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password
        })
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('user', credentials.email);
        localStorage.setItem('authMethod', 'email');
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('rememberMe');
        }
        navigate('/chat', { replace: true });
      } else {
        setError('Invalid email or password');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = () => {
    navigate('/register');
  };

  const handleNextClick = () => {
    if (credentials.email.trim()) {
      setShowPasswordField(true);
      setTimeout(() => passwordInputRef.current?.focus(), 50);
    }
  };

  return (
    <div className="login-main-container">
      <header className="login-header">
        <div className="login-logo">
          <img src={logo} alt="SAVI AI" className="login-logo-image" />
        </div>
        <button className="login-signup-button" onClick={handleSignup}>Sign Up</button>
      </header>
      <div className='login-form-bg'>
        <div className="login-form-container">
          <h1 className="login-form-title">
            Sign in to your <span className="login-title-highlight">Savi</span> 
          </h1>

          {/* Microsoft Login Button */}
          <div className="login-social-container">
            <button
              className="login-microsoft-button"
              onClick={handleMicrosoftLogin}
              disabled={loading}
            >
              <svg width="20" height="20" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1H11V11H1V1Z" fill="#F25022" />
                <path d="M12 1H22V11H12V1Z" fill="#7FBA00" />
                <path d="M1 12H11V22H1V12Z" fill="#00A4EF" />
                <path d="M12 12H22V22H12V12Z" fill="#FFB900" />
              </svg>
              {loading ? 'Signing in...' : 'Sign in with SAVIC Account'}
            </button>
          </div>

          <div className="login-or-divider">
            <span className="login-or-text">OR</span>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="login-input-group">
              <FiMail className="login-input-icon" />
              <input
                type="email"
                name="email"
                value={credentials.email}
                onChange={handleEmailChange}
                onKeyDown={handleKeyDown}
                placeholder="Email address"
                className="login-input-field"
                ref={emailInputRef}
                autoComplete="email"
                disabled={loading}
              />
              {credentials.email && (
                <button
                  type="button"
                  className="login-next-button"
                  onClick={handleNextClick}
                  aria-label="Next to password"
                  disabled={loading}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>

            {showPasswordField && (
              <>
                <div className="login-input-group login-password-field">
                  <FiLock className="login-input-icon" />
                  <input
                    id="password-input"
                    type={isPasswordVisible ? "text" : "password"}
                    name="password"
                    value={credentials.password}
                    onChange={handlePasswordChange}
                    placeholder="Password"
                    className="login-input-field"
                    ref={passwordInputRef}
                    autoComplete="current-password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                    aria-label={isPasswordVisible ? "Hide password" : "Show password"}
                  >
                    {isPasswordVisible ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
                
                <div style={{ marginTop: 8 }}>
                  <button
                    type="submit"
                    className="login-button"
                    disabled={loading || !credentials.password}
                    style={{ width: '100%' }}
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </button>
                </div>
              </>
            )}

            {error && <p className="login-error-message">{error}</p>}

            <div className="login-form-footer">
              <div className="login-remember-me">
                <input
                  type="checkbox"
                  id="remember"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                  disabled={loading}
                />
                <label htmlFor="remember">Remember me</label>
              </div>
              <Link to="/forgot-password" className="login-forgot-password">Forgot password?</Link>
            </div>
          </form>

          <div className="login-signup-prompt">
            Don't have an account? <Link to="/register" className="login-signup-link">Sign Up</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;